import { type FC, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons/faMagnifyingGlass';
import { FormProvider, useForm } from 'FrontRoyalReactHookForm';
import { Input } from 'Input';
import { sanitizeSearchText } from '../filterStreams';
import '../locales/browseCourses/browseCourses-en.json';

const DEBOUNCE_MS = 250;

type Props = {
    // The committed search text. A change from outside, such as Reset, replaces what the input shows.
    value: string;
    onChange: (searchText: string) => void;
};

// Commits 250ms after the user stops typing, or at once on Enter or clear.
export const CourseSearchInput: FC<Props> = ({ value, onChange }) => {
    const { t } = useTranslation('back_royal', { keyPrefix: 'browseCourses.browseCourses' });
    const methods = useForm<{ searchText: string }>({ defaultValues: { searchText: value } });
    const { getValues, setFieldValue, watch } = methods;
    const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
    const lastSent = useRef(value);

    // The parent passes a new onChange on each render. Calling it through a ref keeps the debounce timer
    // alive across those renders and always calls the latest version.
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    const send = useCallback((searchText: string) => {
        clearTimeout(timer.current);
        if (searchText === lastSent.current) return;
        lastSent.current = searchText;
        onChangeRef.current(searchText);
    }, []);

    // The committed value is sanitized. Keep what the user typed when it sanitizes to the same text.
    useEffect(() => {
        if (value === lastSent.current) return;
        lastSent.current = value;
        if (sanitizeSearchText(getValues('searchText')) !== value) setFieldValue('searchText', value);
    }, [value, getValues, setFieldValue]);

    useEffect(() => {
        const subscription = watch(({ searchText }) => {
            clearTimeout(timer.current);
            timer.current = setTimeout(() => send(searchText ?? ''), DEBOUNCE_MS);
        });
        return () => {
            subscription.unsubscribe();
            clearTimeout(timer.current);
        };
    }, [watch, send]);

    return (
        <FormProvider {...methods}>
            <form
                role="search"
                onSubmit={event => {
                    event.preventDefault();
                    send(getValues('searchText'));
                }}
            >
                <Input
                    name="searchText"
                    type="search"
                    label={t('search')}
                    placeholder={t('searchPlaceholder')}
                    startContent={<FontAwesomeIcon icon={faMagnifyingGlass} className="text-slate-grey" />}
                    isClearable
                    onClear={() => send('')}
                    // The library default fill is made for white containers. This page has a grey background.
                    classNames={{
                        inputWrapper:
                            'bg-white hover:bg-white group-data-[focus=true]:bg-white border border-solid border-slate-grey-200 shadow-none',
                    }}
                />
            </form>
        </FormProvider>
    );
};
